import { ISuggestion } from '@fly/shared';
import { RootState } from '@fly/store';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import log from 'loglevel';

import type { GqlApiSliceTag } from './gqlApiSlice';

const SESSION_STORAGE_CHAT_OPEN_KEY = 'fly-chat.open';
const SESSION_STORAGE_THREAD_KEY = 'fly-chat.thread-id';
const MAX_QUEUED_MESSAGES = 10;

const setSessionStorageChatOpen = (value: boolean) => {
  try {
    sessionStorage.setItem(
      SESSION_STORAGE_CHAT_OPEN_KEY,
      value ? 'true' : 'false'
    );
  } catch (err) {
    log.warn('Failed to persist chat open state:', err);
  }
};
const getSessionStorageChatOpen = () => {
  if (typeof sessionStorage === 'undefined') return true;
  try {
    return sessionStorage.getItem(SESSION_STORAGE_CHAT_OPEN_KEY) !== 'false';
  } catch (err) {
    log.warn('Failed to read chat open state:', err);
    return true;
  }
};

export const getStoredThreadId = (): string | null => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(SESSION_STORAGE_THREAD_KEY);
  } catch (err) {
    log.warn('Failed to read thread id:', err);
    return null;
  }
};

export const setStoredThreadId = (threadId: string): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_THREAD_KEY, threadId);
  } catch (err) {
    log.warn('Failed to persist thread id:', err);
  }
};

export const clearStoredThreadId = (): void => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_THREAD_KEY);
  } catch (err) {
    log.warn('Failed to clear thread id:', err);
  }
};

export interface ChatState {
  isOpen: boolean;
  threadId: string | null;
  error: string | null;
  isStreaming: boolean;
  isLoading: boolean;
  suggestions: Array<ISuggestion>;
  targetRoute: string | null;
  startNavigate: boolean;
  pendingCacheInvalidation: GqlApiSliceTag[];
  queuedMessages: string[];
}

const initSuggestions = [
  { id: '1', content: 'What is Fly' },
  { id: '2', content: 'How to upload an artifact' },
  { id: '3', content: 'How to configure my CI' },
];

const initialState: ChatState = {
  isOpen: getSessionStorageChatOpen(),
  threadId: getStoredThreadId(),
  isLoading: false,
  error: null,
  isStreaming: false,
  suggestions: [],
  targetRoute: null,
  startNavigate: false,
  pendingCacheInvalidation: [],
  queuedMessages: [],
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    clearAutonNavigation: (state) => {
      return {
        ...state,
        targetRoute: null,
      };
    },
    setStartNavigate: (state, action) => {
      return {
        ...state,
        startNavigate: action.payload,
      };
    },
    toggleChat: (state) => {
      const isOpen = state.isOpen;
      if (isOpen) {
        setSessionStorageChatOpen(false);
      } else {
        setSessionStorageChatOpen(true);
      }
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    },
    openChat: (state, action) => {
      setSessionStorageChatOpen(true);
      return {
        ...state,
        isOpen: true,
      };
    },
    closeChat: (state) => {
      setSessionStorageChatOpen(false);
      return {
        ...state,
        isOpen: false,
      };
    },
    setSuggestions: (state, action) => {
      return {
        ...state,
        suggestions: [...action.payload],
      };
    },
    setGeneralSuggestions: (state) => {
      return {
        ...state,
        suggestions: initSuggestions,
      };
    },
    clearSuggestions: (state) => {
      return {
        ...state,
        suggestions: [],
      };
    },
    setError: (state, action) => {
      return { ...state, error: action.payload, isStreaming: false };
    },
    clearError: (state) => {
      return { ...state, error: null };
    },
    setIsStreaming: (state, action) => {
      return { ...state, isStreaming: action.payload };
    },
    setTargetRoute: (state, action) => {
      return { ...state, targetRoute: action.payload };
    },
    addPendingCacheInvalidation: (state, action) => {
      return {
        ...state,
        pendingCacheInvalidation: [
          ...new Set([...state.pendingCacheInvalidation, ...action.payload]),
        ],
      };
    },
    clearPendingCacheInvalidation: (state) => {
      return { ...state, pendingCacheInvalidation: [] };
    },
    enqueueQueuedMessage: (state, action) => {
      const message = `${action.payload || ''}`.trim();
      if (!message) {
        return state;
      }

      return {
        ...state,
        queuedMessages: [
          ...state.queuedMessages.slice(-(MAX_QUEUED_MESSAGES - 1)),
          message,
        ],
      };
    },
    dequeueQueuedMessage: (state) => {
      if (!state.queuedMessages.length) {
        return state;
      }
      return {
        ...state,
        queuedMessages: state.queuedMessages.slice(1),
      };
    },
    removeQueuedMessageAtIndex: (state, action) => {
      const index = action.payload;
      if (
        typeof index !== 'number' ||
        index < 0 ||
        index >= state.queuedMessages.length
      ) {
        return state;
      }

      return {
        ...state,
        queuedMessages: state.queuedMessages.filter((_, i) => i !== index),
      };
    },
    clearQueuedMessages: (state) => {
      return { ...state, queuedMessages: [] };
    },
    setThreadId: (state, action) => {
      if (action.payload) {
        setStoredThreadId(action.payload);
      } else {
        clearStoredThreadId();
      }

      return {
        ...state,
        threadId: action.payload,
      };
    },
  },
});

export const selectChat = (state: RootState) => state.chat;

export const selectCurrentThreadId = createSelector(
  selectChat,
  (chat) => chat?.threadId
);

export const selectIsChatOpen = createSelector(
  selectChat,
  (chat) => chat.isOpen
);

export const selectSuggestions = createSelector(selectChat, (chat) =>
  chat.suggestions.slice(-3)
);
export const selectQueuedMessages = createSelector(
  selectChat,
  (chat) => chat.queuedMessages
);

export const selectAutoNavigation = createSelector(
  selectChat,
  (chat) => chat.targetRoute
);

export const selectStartNavigate = createSelector(
  selectChat,
  (chat) => chat.startNavigate
);

export const selectPendingCacheInvalidation = createSelector(
  selectChat,
  (chat) => chat.pendingCacheInvalidation
);

export const {
  openChat,
  closeChat,
  toggleChat,
  setStartNavigate,
  setThreadId,
  setError,
  setIsStreaming,
  setTargetRoute,
  addPendingCacheInvalidation,
  setSuggestions,
  setGeneralSuggestions,
  clearSuggestions,
  clearAutonNavigation,
  clearError,
  clearPendingCacheInvalidation,
  enqueueQueuedMessage,
  dequeueQueuedMessage,
  removeQueuedMessageAtIndex,
  clearQueuedMessages,
} = chatSlice.actions;
export default chatSlice.reducer;
